import { useEffect, useMemo, useState } from "react"
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  InputAdornment,
  Typography
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import { usePermissionsTreeViewItems } from "src/modules/system/roles/hooks/usePermissionsTreeViewItems"
import { Stack } from "src/shared/components"
import OutlinedInput from "src/shared/components/outlined-input"
import Switch from "src/shared/components/switch"
import TitleWithBackButton from "src/shared/container/title-with-back-button"
import ROUTE_PATHS from "src/routes/route-path"
import { Controller, useForm } from "react-hook-form"
import { RoleFormParam } from "../../roles.type"
import { roleInitialValues, roleValidationSchema } from "../../role.schema"
import { yupResolver } from "@hookform/resolvers/yup"
import { Status } from "src/graphql/quark/quark.type"
import { useTranslation } from "react-i18next"

const RoleForm = ({
  roleValues,
  onSubmit,
  isSubmitting
}: {
  roleValues?: RoleFormParam
  onSubmit: (val: RoleFormParam) => void
  isSubmitting?: boolean
}) => {
  const { t } = useTranslation("system")
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [search, setSearch] = useState("")
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null)
  const { modules, allActionIds } = usePermissionsTreeViewItems()
  const roleForm = useForm<RoleFormParam>({
    resolver: yupResolver(roleValidationSchema),
    defaultValues: roleValues || roleInitialValues,
    values: roleValues || roleInitialValues
  })
  const roleName = roleForm.watch("name")
  const selectedSet = useMemo(() => new Set(selectedItems), [selectedItems])

  useEffect(() => {
    if (roleValues?.id) {
      setSelectedItems((roleValues.permissions || []) as string[])
    }
  }, [roleValues])

  useEffect(() => {
    if (!modules.length) return
    if (!activeModuleId || !modules.some((module) => module.id === activeModuleId)) {
      setActiveModuleId(modules[0].id)
    }
  }, [modules, activeModuleId])

  const filteredModules = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return modules

    return modules
      .map((module) => {
        const moduleMatch = module.label.toLowerCase().includes(query)
        const subModules = module.subModules
          .map((subModule) => {
            const subMatch = subModule.label.toLowerCase().includes(query)
            const actions = subModule.actions.filter(
              (action) => subMatch || moduleMatch || action.label.toLowerCase().includes(query)
            )
            if (!actions.length && !subMatch && !moduleMatch) return null
            return {
              ...subModule,
              actions: moduleMatch || subMatch ? subModule.actions : actions
            }
          })
          .filter(Boolean) as typeof module.subModules

        if (!subModules.length && !moduleMatch) return null
        return {
          ...module,
          subModules: moduleMatch ? module.subModules : subModules,
          actionIds: (moduleMatch ? module.subModules : subModules).flatMap((sub) =>
            sub.actions.map((action) => action.id)
          )
        }
      })
      .filter(Boolean) as typeof modules
  }, [modules, search])

  useEffect(() => {
    if (!filteredModules.length) {
      setActiveModuleId(null)
      return
    }
    if (!filteredModules.some((module) => module.id === activeModuleId)) {
      setActiveModuleId(filteredModules[0].id)
    }
  }, [filteredModules, activeModuleId])

  const activeModule = filteredModules.find((module) => module.id === activeModuleId) ?? null

  const applySelection = (ids: string[]) => {
    setSelectedItems(ids)
    roleForm.setValue("permissions", ids, { shouldValidate: true })
  }

  const toggleAction = (actionId: string) => {
    if (selectedSet.has(actionId)) {
      applySelection(selectedItems.filter((id) => id !== actionId))
      return
    }
    applySelection([...selectedItems, actionId])
  }

  const toggleModule = (actionIds: string[], checked: boolean) => {
    const actionSet = new Set(actionIds)
    if (checked) {
      applySelection([...selectedItems.filter((id) => !actionSet.has(id)), ...actionIds])
      return
    }
    applySelection(selectedItems.filter((id) => !actionSet.has(id)))
  }

  const handleSubmit = () => {
    roleForm.handleSubmit(onSubmit)()
  }

  return (
    <Stack>
      <TitleWithBackButton
        href={ROUTE_PATHS.ROLES.BASE}
        title={`${roleValues?.id ? "Update" : "Create"} ${roleName} Role`}
      />

      <Stack
        // sx={{ border: 1, borderColor: "divider", borderRadius: "12px", p: 2 }}
        mb={2}
        direction="row"
        alignItems="flex-end"
        gap={2}
      >
        <Controller
          name="name"
          control={roleForm.control}
          render={({ field, fieldState }) => (
            <OutlinedInput
              onChange={field.onChange}
              value={field.value}
              placeholder={t("roles.form.namePlaceholder")}
              label={t("roles.form.name")}
              fullWidth
              required
              sx={{ width: { xs: "100%", md: "70%", lg: "50%" } }}
              error={!!fieldState?.error}
              helperText={fieldState?.error?.message}
            />
          )}
        />
        <Controller
          name="status"
          control={roleForm.control}
          render={({ field, fieldState }) => (
            <Switch
              required
              onChange={(val) => field.onChange(val ? Status.Active : Status.Inactive)}
              checked={field.value === Status.Active}
              title={t("roles.form.status")}
              subtitle="InActive/Active"
              error={!!fieldState?.error}
              helperText={fieldState?.error?.message}
            />
          )}
        />
      </Stack>
      <Divider sx={{ my: 2 }} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} gap={1} flexWrap="wrap">
        <Typography variant="body1" fontWeight={700}>
          {t("roles.form.permissions")}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {t("roles.form.selectedCount", { selected: selectedItems.length, total: allActionIds.length })}
          </Typography>
        </Typography>
        <Stack direction="row" gap={1}>
          <Button size="small" variant="text" onClick={() => applySelection(allActionIds)}>
            {t("roles.form.selectAll")}
          </Button>
          <Button size="small" variant="text" color="inherit" onClick={() => applySelection([])}>
            {t("roles.form.clearAll")}
          </Button>
        </Stack>
      </Stack>

      <OutlinedInput
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t("roles.form.searchPermissions")}
        fullWidth
        size="small"
        sx={{ mb: 1.5 }}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "200px 1fr" },
          border: 1,
          borderColor: "divider",
          borderRadius: "12px",
          overflow: "hidden",
          minHeight: 280,
          maxHeight: 500,
          mb: 2
        }}
      >
        <Box
          sx={{
            borderRight: { md: 1 },
            borderBottom: { xs: 1, md: 0 },
            borderColor: "divider",
            overflowY: "auto",
            bgcolor: "action.hover"
          }}
        >
          {filteredModules.length ? (
            filteredModules.map((module) => {
              const selectedCount = module.actionIds.filter((id) => selectedSet.has(id)).length
              const isActive = module.id === activeModuleId
              return (
                <Box
                  key={module.id}
                  onClick={() => setActiveModuleId(module.id)}
                  sx={{
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    bgcolor: isActive ? "background.paper" : "transparent",
                    borderLeft: 3,
                    borderColor: isActive ? "primary.main" : "transparent",
                    "&:hover": { bgcolor: "background.paper" }
                  }}
                >
                  <Typography variant="body2" fontWeight={isActive ? 700 : 500} noWrap>
                    {module.label}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {selectedCount}/{module.actionIds.length}
                  </Typography>
                </Box>
              )
            })
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {t("roles.form.noPermissions")}
            </Typography>
          )}
        </Box>

        <Box sx={{ overflowY: "auto", p: 1.5 }}>
          {activeModule ? (
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography variant="body1" fontWeight={700}>
                  {activeModule.label}
                </Typography>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={
                        activeModule.actionIds.length > 0 && activeModule.actionIds.every((id) => selectedSet.has(id))
                      }
                      indeterminate={
                        activeModule.actionIds.some((id) => selectedSet.has(id)) &&
                        !activeModule.actionIds.every((id) => selectedSet.has(id))
                      }
                      onChange={(event) => toggleModule(activeModule.actionIds, event.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">{t("roles.form.selectModule")}</Typography>}
                />
              </Stack>

              {activeModule.subModules.map((subModule) => (
                <Box key={subModule.id}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      {subModule.label}
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ minWidth: 0, px: 0.5, fontSize: "0.7rem" }}
                      onClick={() => {
                        const ids = subModule.actions.map((action) => action.id)
                        const allSelected = ids.every((id) => selectedSet.has(id))
                        toggleModule(ids, !allSelected)
                      }}
                    >
                      {subModule.actions.every((action) => selectedSet.has(action.id))
                        ? t("roles.form.clearAll")
                        : t("roles.form.selectAll")}
                    </Button>
                  </Stack>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {subModule.actions.map((action) => {
                      const selected = selectedSet.has(action.id)
                      return (
                        <Chip
                          key={action.id}
                          size="small"
                          clickable
                          label={action.label}
                          color={selected ? "primary" : "default"}
                          variant={selected ? "filled" : "outlined"}
                          onClick={() => toggleAction(action.id)}
                          sx={{ borderRadius: "8px", height: 26 }}
                        />
                      )
                    })}
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              {t("roles.form.noPermissions")}
            </Typography>
          )}
        </Box>
      </Box>

      <Stack direction="row" justifyContent="flex-end">
        <Button disabled={isSubmitting} onClick={handleSubmit} variant="contained" color="primary">
          {isSubmitting ? (
            <CircularProgress sx={{ color: (theme) => theme.palette.primary.contrastText }} size="1rem" />
          ) : roleValues?.id ? (
            t("roles.form.updateButton")
          ) : (
            t("roles.form.createButton")
          )}
        </Button>
      </Stack>
    </Stack>
  )
}

export default RoleForm
